import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Heart, ExternalLink, Share2, ShieldCheck, Quote, MessageCircle, Utensils, HeartHandshake, Wind } from "lucide-react";
import { HubPage, Container, BetaCTA, CommunityCTA, Band, Seo } from "../components/hub/HubKit";
import { communityStoryBySlug } from "../data/hub";
import { track } from "../lib/analytics";

// Distilled, elegant "ways to show up" cards (presentation of the story's guidance)
const SUPPORT_WAYS = [
  { icon: MessageCircle, title: "Check in", note: "A simple message that asks nothing in return." },
  { icon: Utensils, title: "Help practically", note: "A meal, an errand, a ride to an appointment." },
  { icon: HeartHandshake, title: "Be present", note: "Listen and sit with them — without trying to fix." },
  { icon: Wind, title: "Give space", note: "Respect their need for rest and quiet, too." },
];

const PermissionChip = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-cream/80 px-3 py-1.5 text-[0.62rem] font-medium uppercase tracking-[0.14em] text-sage-deep" data-testid="story-permission">
    <ShieldCheck size={13} /> Real story · Shared with permission
  </span>
);

export default function CommunityStory() {
  const { slug } = useParams();
  const story = communityStoryBySlug(slug);
  if (!story) return <Navigate to="/community" replace />;
  const { support } = story;
  const sections = story.sections || [];
  const showingUp = sections.find((s) => /showing up/i.test(s.heading));
  const narrative = sections.filter((s) => s !== showingUp && !/supporting/i.test(s.heading));

  const onShare = async () => {
    track("story_shared", story.slug);
    try {
      if (navigator.share) await navigator.share({ title: story.title, url: window.location.href });
      else await navigator.clipboard.writeText(window.location.href);
    } catch (e) { /* ignore */ }
  };

  return (
    <HubPage testid={`community-story-${slug}`}>
      <Seo title={`${story.title} — HEARD Community Story`} description={story.excerpt} />

      {/* Editorial split hero — photo + Meet Troy, integrated */}
      <Band tone="cream">
        <Container className="pt-8 pb-12 sm:pt-10 sm:pb-16">
          <Link to="/community" className="inline-flex items-center gap-1.5 text-sm text-sage-deep hover:opacity-70" data-testid="story-back">
            <ArrowLeft size={15} /> The HEARD Community
          </Link>
          <div className="mt-6 grid items-center gap-8 lg:grid-cols-[0.82fr_1fr] lg:gap-12">
            {story.image && (
              <div className="rounded-[1.4rem] border border-line bg-cream-deep/50 p-2 shadow-[0_16px_44px_rgba(47,75,57,0.12)]">
                <img src={story.image} alt={`${story.subject || "Troy"} with family`} className="w-full rounded-[1rem]" data-testid="story-image" />
              </div>
            )}
            <div>
              <span className="text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-[#B98A4E]">Meet Troy</span>
              <h1 className="serif-display mt-3 text-[2rem] font-normal leading-[1.12] text-charcoal sm:text-[2.7rem]">{story.title}</h1>
              <p className="mt-3 text-lg leading-relaxed text-sage-deep">{story.subtitle}</p>
              <p className="mt-5 text-base leading-relaxed text-ink/75">{story.intro}</p>
              <div className="mt-6"><PermissionChip /></div>
            </div>
          </div>
        </Container>
      </Band>

      {/* Narrative section(s) — "When life changes quickly" */}
      {narrative.map((sec) => (
        <Band key={sec.heading} tone="sage" className="border-y border-line/60 py-10 sm:py-12">
          <Container>
            <section className="mx-auto max-w-3xl text-center" data-testid="story-section">
              <div className="mx-auto mb-5 flex items-center justify-center gap-3">
                <span className="h-px w-10 bg-[#E3D6BC]" />
                <img src="/heard-sprig.png" alt="" className="h-5 w-auto opacity-80" />
                <span className="h-px w-10 bg-[#E3D6BC]" />
              </div>
              <h2 className="serif-display text-[1.6rem] font-normal text-charcoal sm:text-[2rem]">{sec.heading}</h2>
              <div className="mt-4 space-y-4 text-[1.05rem] leading-[1.8] text-ink/80">
                {(Array.isArray(sec.body) ? sec.body : [sec.body]).filter(Boolean).map((p, k) => <p key={k}>{p}</p>)}
              </div>
            </section>
          </Container>
        </Band>
      ))}

      {/* "What showing up can look like" — support-way cards */}
      {showingUp && (
        <Band tone="cream" className="py-10 sm:py-12">
          <Container>
            <section className="mx-auto max-w-5xl" data-testid="story-section">
              <div className="mx-auto mb-9 max-w-2xl text-center">
                <div className="mx-auto mb-4 flex items-center justify-center gap-3">
                  <span className="h-px w-8 bg-[#E3D6BC]" />
                  <img src="/heard-sprig.png" alt="" className="h-4 w-auto opacity-75" />
                  <span className="h-px w-8 bg-[#E3D6BC]" />
                </div>
                <h2 className="serif-display text-[1.5rem] font-normal text-charcoal sm:text-[1.9rem]">{showingUp.heading}</h2>
                <p className="mt-3 text-base leading-relaxed text-ink/65">Support doesn't have to be grand — often it's the small, steady things.</p>
              </div>
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
                {SUPPORT_WAYS.map((w) => {
                  const Icon = w.icon;
                  return (
                    <div key={w.title} className="rounded-[1.1rem] border border-sage/15 bg-sage/[0.05] p-5 transition-all duration-500 hover:-translate-y-0.5 hover:bg-sage/[0.09]">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/70 text-[#B98A4E]"><Icon size={16} strokeWidth={1.9} /></span>
                      <h3 className="serif-display mt-3.5 text-base text-charcoal">{w.title}</h3>
                      <p className="mt-1.5 text-[0.82rem] leading-relaxed text-ink/60">{w.note}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          </Container>
        </Band>
      )}

      {/* Pull quote — a visual moment, close to the section above */}
      <Band tone="cream" className="pb-12 pt-2 sm:pb-16 sm:pt-4">
        <Container>
          <blockquote className="mx-auto max-w-3xl text-center">
            <Quote size={24} className="mx-auto text-[#B98A4E]" />
            <p className="serif-display mt-4 text-[1.5rem] font-normal italic leading-[1.4] text-sage-deep sm:text-[2rem]">
              Support doesn't have to be grand. Often, it's the small, steady things that carry someone through.
            </p>
          </blockquote>
        </Container>
      </Band>

      {/* Emotional closing — Supporting Troy */}
      {support?.url && (
        <Band tone="sage" className="py-12 sm:py-14">
          <Container>
            <div className="mx-auto max-w-4xl text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#B98A4E]/15 text-[#B98A4E]"><Heart size={22} strokeWidth={1.8} /></span>
              <h2 className="serif-display mt-4 text-[1.7rem] leading-snug text-charcoal sm:text-[2.2rem]">A little support can make room for healing.</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-ink/70">
                Every donation, share, message, and act of kindness gives Troy and his family a little more room to focus on what matters most right now.
              </p>

              <div className="mt-9 grid items-center gap-8 rounded-[1.8rem] border border-line bg-cream/85 p-6 text-left shadow-[0_20px_54px_rgba(47,75,57,0.09)] sm:p-9 lg:grid-cols-[1fr_auto]" data-testid="story-support">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                  {story.image && (
                    <img src={story.image} alt={story.subject || "Troy"} className="h-24 w-24 shrink-0 rounded-2xl border border-line object-cover object-center" />
                  )}
                  <div>
                    <h3 className="serif-display text-2xl text-charcoal">{support.name}</h3>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <a
                        href={support.url} target="_blank" rel="noreferrer noopener"
                        onClick={() => track("support_troy_clicked", story.slug)}
                        data-testid="story-support-button"
                        className="inline-flex items-center gap-2 rounded-full bg-sage-deep px-7 py-3.5 text-sm font-medium text-cream transition-transform hover:-translate-y-0.5"
                      >
                        {support.name} <ExternalLink size={15} />
                      </a>
                      <button
                        onClick={onShare}
                        data-testid="story-share-button"
                        className="inline-flex items-center gap-2 rounded-full border border-sage-deep px-7 py-3.5 text-sm font-medium text-sage-deep transition-colors hover:bg-sage-deep hover:text-cream"
                      >
                        <Share2 size={15} /> Share Troy's Story
                      </button>
                    </div>
                  </div>
                </div>
                {support.qr && (
                  <div className="mx-auto shrink-0 text-center sm:mx-0">
                    <div className="rounded-2xl border border-line bg-white p-2.5">
                      <img src={support.qr} alt="Scan to open Troy's GoFundMe" width="120" height="120" className="block rounded-lg" data-testid="story-qr" />
                    </div>
                    <p className="mt-2 text-[0.62rem] uppercase tracking-[0.16em] text-taupe">Scan to give</p>
                  </div>
                )}
              </div>

              {support.note && <p className="mx-auto mt-5 max-w-2xl text-xs leading-relaxed text-ink/50">{support.note}</p>}
            </div>
          </Container>
        </Band>
      )}

      <Band tone="deep" className="py-12 sm:py-14">
        <Container>
          <div className="mx-auto max-w-3xl"><CommunityCTA compact /></div>
        </Container>
      </Band>
    </HubPage>
  );
}
